import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DocRenderer from "./DocRenderer";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { Navbar } from "./Navbar.jsx";
import AIProviderToggle from "./AIProviderToggle";

const UNLOCKS = [
  "Full list of missing keywords (ranked by importance)",
  "Section-by-section AI recommendations",
  "Keyword approval & custom keyword input",
  "ATS-optimised resume generated by AI",
  "Download as PDF or Word (4 templates)",
  "Live resume editing with Claude chat",
];

function PartScore() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { data, jobDescriptionFile, jobDescriptionText, resumeFile } = location.state || {};

  const [showResume,  setShowResume]  = useState(true);
  const [screenWidth,  setScreenWidth]  = useState(window.innerWidth);
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);

  useEffect(() => {
    const onResize = () => { setScreenWidth(window.innerWidth); setScreenHeight(window.innerHeight); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handlePaywall = () => {
    const steps = JSON.parse(localStorage.getItem("completedSteps") || "[]");
    if (!steps.includes("partScore")) { steps.push("partScore"); localStorage.setItem("completedSteps", JSON.stringify(steps)); }
    navigate("/pay", { state: { data, jobDescriptionFile, jobDescriptionText, resumeFile } });
  };

  const score            = data?.score || 0;
  const scoreInt         = Math.round(score);
  const totalMissing     = data?.missing_keywords_length || 0;
  const visibleKeywords  = data?.partial_missing_keywords || [];
  const blurredCount     = totalMissing - visibleKeywords.length;

  const sessionId = data?.session_id;
  if (sessionId) localStorage.setItem("sessionId", sessionId);

  const scoreColor = scoreInt >= 70 ? "#2c5141" : scoreInt >= 40 ? "#b08d57" : "#6f2a20";
  const scoreLabel = scoreInt >= 70 ? "Strong Match" : scoreInt >= 40 ? "Needs Improvement" : "Low Match";

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white pb-16">
        <div className="max-w-6xl mx-auto px-4 pt-8">

          <div className="flex flex-col lg:flex-row gap-8">

            {/* ── Left column: score + keywords + paywall ── */}
            <div className="flex flex-col gap-5 lg:w-1/2">

              {/* Score card */}
              <div className="bg-white shadow-md rounded-2xl p-6 text-center">
                <h2 className="text-lg font-semibold text-gray-700 mb-4">Your ATS Match Score</h2>
                <div className="flex justify-center items-center mb-4">
                  <div className="relative w-36 h-36">
                    <div
                      className="w-full h-full rounded-full"
                      style={{
                        background: `conic-gradient(${scoreColor} ${scoreInt * 3.6}deg, #e3dccd ${scoreInt * 3.6}deg)`,
                      }}
                    />
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-bold text-gray-900">{scoreInt}%</span>
                      <span className="text-xs text-gray-500 mt-0.5">{scoreLabel}</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-500 text-sm">
                  {scoreInt >= 70
                    ? "Great start — unlock the full report to get to 90%+."
                    : "Your resume is missing key terms the ATS is scanning for. Unlock to see them all."}
                </p>
                {/* Engine used for this analysis — locked once the result is in */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-center">
                  <AIProviderToggle locked />
                </div>
              </div>

              {/* Partial keywords */}
              <div className="bg-white shadow-md rounded-2xl p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-gray-800">Missing Keywords Preview</h3>
                  <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2.5 py-1 rounded-full">
                    {totalMissing} total
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {visibleKeywords.map((kw, i) => (
                    <span key={i} className="bg-red-50 text-red-700 border border-red-200 text-xs font-medium px-3 py-1 rounded-full">
                      {kw}
                    </span>
                  ))}
                  {[...Array(Math.min(blurredCount, 8))].map((_, i) => (
                    <span
                      key={`blur-${i}`}
                      className="bg-gray-100 text-gray-400 text-xs px-3 py-1 rounded-full"
                      style={{ filter: "blur(5px)", userSelect: "none" }}
                    >
                      keyword
                    </span>
                  ))}
                  {blurredCount > 8 && (
                    <span className="text-xs text-gray-400 self-center">+{blurredCount - 8} more hidden</span>
                  )}
                </div>
              </div>

              {/* Paywall unlock card */}
              <div className="bg-white shadow-md rounded-2xl p-6 border-2 border-blue-100">
                <h3 className="font-bold text-gray-800 text-lg mb-1">Unlock the Full Report</h3>
                <p className="text-gray-500 text-sm mb-4">One payment · instant access · all features included.</p>

                <ul className="space-y-2 mb-6">
                  {UNLOCKS.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center text-white text-[10px] flex-shrink-0">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={handlePaywall}
                  className="w-full py-3 rounded-xl bg-blue-600 text-white font-bold text-base hover:bg-blue-500 transition shadow"
                >
                  Unlock Full Report — $1.99
                </button>

                <div className="mt-4 text-center">
                  <span className="text-gray-400 text-xs">Not ready to pay?  </span>
                  <button
                    onClick={() => navigate("/appointment")}
                    className="text-xs text-blue-500 hover:underline font-medium"
                  >
                    Book a free 10-min expert review instead →
                  </button>
                </div>
              </div>
            </div>

            {/* ── Right column: document preview ── */}
            <div className="lg:w-1/2">
              <div className="bg-white shadow-md rounded-2xl p-5">
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setShowResume(true)}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium transition ${showResume ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                  >
                    My Resume
                  </button>
                  <button
                    onClick={() => setShowResume(false)}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium transition ${!showResume ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                  >
                    Job Description
                  </button>
                </div>
                <div className="h-[60vh] overflow-auto border border-gray-100 rounded-xl p-3">
                  {showResume
                    ? resumeFile
                      ? <DocRenderer file={resumeFile} key={`${screenWidth}-${screenHeight}`} />
                      : <p className="text-gray-400 text-center pt-8">No resume uploaded.</p>
                    : jobDescriptionFile
                    ? <DocRenderer file={jobDescriptionFile} key={`${screenWidth}-${screenHeight}`} />
                    : jobDescriptionText
                    ? <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{jobDescriptionText}</p>
                    : <p className="text-gray-400 text-center pt-8">No job description uploaded.</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PartScore;
